<template>
  <div data-action-sheet class="bottom-wrapper-action-sheet">
    <div
      v-if="cancelableRequest && !showCancelRequestConfirmation"
      v-track-click="BTN_EVTS.CONNECT_REQUEST_CANCEL_TRASH"
      class="cancel-request-icon"
      @click="toggleCancelRequestPrompt"
    >
      <TbTrashXIcon class="cancel-icon" />
    </div>
    <div :class="['button-section', { close }]">
      <div class="arrow-container" @click="toggle">
        <span :class="['arrow', { close }]" />
      </div>
      <template v-if="showCancelRequestConfirmation">
        <div class="form">
          <div class="center title-wrapper">
            <div class="text-center">
              {{ tConversation('chat.sys.cancel_support_request.confirmation') }}
            </div>
          </div>
        </div>
        <div class="button-section__items">
          <div
            v-track-click="BTN_EVTS.CONNECT_REQUEST_CANCEL_NO_CANCEL"
            class="secondary"
            @click="toggleCancelRequestPrompt"
          >
            {{ tConversation('global.no') }}
          </div>
          <div
            v-track-click="BTN_EVTS.CONNECT_REQUEST_CANCEL_YES_CANCEL"
            class="secondary"
            @click="$emit('support-request:cancel')"
          >
            {{ tConversation('page.issue.transaction.cancel_request') }}
          </div>
        </div>
      </template>
      <template v-else-if="!errInfo.isErr">
        <slot />
        <div class="button-section__items">
          <template v-if="!isFixed && !showLoading">
            <div v-if="showBack" class="secondary" @click="$emit('back')">
              {{ tConversation('global.back') }}
            </div>
            <div
              v-for="button in buttons"
              :key="button.id"
              :class="`${button.className} ${button.disabled ? 'disabled' : ''}`"
              @click="handleClick(button)"
            >
              {{ button.name }}
              <component :is="button.appendIcon" v-if="button.appendIcon" class="append-icon" />
            </div>
          </template>
        </div>
        <slot name="behind-button" />
      </template>
      <template v-else>
        <div class="bottom__retry">
          <div>{{ errorMsg }}</div>
        </div>
        <div class="button-section__items" @click="retry">
          <div>
            {{ tConversation('page.bottom_wrapper.retry') }}
          </div>
        </div>
      </template>
      <div v-if="showLoading" class="loading-container">
        <LoadingIcon />
        <div class="loading-text">
          {{ loadingText }}
        </div>
      </div>
    </div>
    <div
      v-if="isFixed && !close && !showLoading && !showCancelRequestConfirmation"
      :class="['button-section__items', { fix: isFixed }]"
    >
      <div
        v-for="button in buttons"
        :key="button.id"
        :class="`${button.className} ${button.disabled ? 'disabled' : ''}`"
        @click="handleClick(button)"
      >
        {{ button.name }}
        <component :is="button.appendIcon" v-if="button.appendIcon" class="append-icon" />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import LoadingIcon from '@/components/icons/LoadingIcon.vue';
import TbTrashXIcon from '@/components/icons/TbTrashXIcon.vue';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import trackClick, { BTN_EVTS } from '@/directives/trackClick';

export interface Button {
  name: string;
  id: string;
  className?: string;
  isActive: Boolean;
  appendIcon?: any;
  disabled?: boolean;
}

@Component({ components: { LoadingIcon, TbTrashXIcon }, directives: { trackClick } })
export class BottomWrapper extends Vue {
  @Prop({ default: () => [] }) readonly buttons!: Button[];
  @Prop({
    default: () => ({
      isErr: false,
      message: '',
    }),
  })
  readonly errInfo!: { isErr: boolean; message: string };
  close = false;
  @Prop(Boolean) readonly isFixed!: boolean;
  @Prop(Boolean) readonly showBack!: boolean;
  @Prop(Boolean) readonly showLoading!: boolean;
  @Prop(String) readonly loadingText!: string;
  @Prop(Boolean) readonly cancelableRequest!: boolean;

  readonly BTN_EVTS = BTN_EVTS;

  showCancelRequestConfirmation = false;

  get errorMsg() {
    if (!this.errInfo.isErr) return '';
    return this.errInfo.message ? this.errInfo.message : this.tConversation('page.bottom_wrapper.tech_issue');
  }

  toggle() {
    this.close = !this.close;
  }
  handleClick(button: Button) {
    if (button.disabled) return;
    this.$emit('select', button);
  }
  retry() {
    this.$emit('retry');
  }

  toggleCancelRequestPrompt() {
    this.showCancelRequestConfirmation = !this.showCancelRequestConfirmation;
    if (this.showCancelRequestConfirmation && this.close) {
      this.toggle();
    }
  }

  @Watch('cancelableRequest')
  resetCancelConfirmation() {
    if (!this.cancelableRequest) {
      this.showCancelRequestConfirmation = false;
    }
  }
}

export default BottomWrapper;
</script>

<style lang="scss">
@import '@/assets/scss/base.scss';
@import '@/assets/scss/variable.scss';

.bottom-wrapper-action-sheet {
  background: $action-sheet-background;
  color: $action-sheet-text-color;
}

.button-section {
  // min-height: 190px;
  max-height: 50vh;
  overflow-y: auto;
  box-shadow: 0 -3px 5px 0 rgba(0, 0, 0, 0.15);
  padding-left: 0.02vw;
  padding-right: 0.02vw;
  padding-bottom: 2rem;
  transition: all 0.25s ease;
  .bottom {
    &__question {
      @extend .text-bold;
      color: $input-form-title-color;
      text-align: center;
      font-size: $size-m;
      &.trans-empty {
        color: #e41827;
        padding-bottom: 2rem;
        font-style: italic;
      }
      &:not(:last-child) {
        margin-bottom: $size-m;
      }
    }
    &__retry {
      display: flex;
      justify-content: center;
      align-items: center;
      font-size: $size-m;
      text-align: center;
      padding-bottom: 16px;
      line-height: 2.4rem;
      color: $input-form-title-color;
      div {
        max-width: 28.4rem;
      }
    }
  }
  &.close {
    height: 3rem;
    padding-bottom: 0.5rem;
    .arrow-container {
      padding-top: 10px;
    }
  }
  &__items {
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    gap: 8px;
    & > div {
      @extend .text-medium;
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 8px;
      color: $filled-button-text-color;
      background-color: $filled-button-background;
      border-radius: 0.2rem;
      padding: 1rem 1.6rem;
      font-size: $size-m2;
      cursor: pointer;
      &:not(:last-child) {
        margin-right: 0.41rem;
      }
      &:active {
        background-color: $filled-button-background;
        color: $filled-button-text-color;
      }
    }
    & > div.secondary {
      background: $outline-button-background;
      color: $outline-button-text-color;
      border: 1px solid $outline-button-outline-color;
    }

    & > div.disabled {
      color: $filled-button-text-color--disabled;
      background-color: $filled-button-background--disabled;
      border: 1px solid $outline-button-outline-color--disabled;
      cursor: not-allowed;
    }
    &.fix {
      margin-top: 1rem;
      padding-bottom: 2rem;
    }
  }
  .arrow-container {
    display: flex;
    justify-content: center;
    align-items: center;
    padding-top: 5px;
    padding-bottom: 2px;
    margin-bottom: 15px;
    .arrow {
      border: solid $action-sheet-expand-arrow-color;
      border-width: 0 3px 3px 0;
      display: inline-block;
      transform: rotate(45deg);
      padding: 4.87px;
      transition: transform 0.2s ease-in-out;
      &.close {
        transform: rotate(225deg);
      }
    }
  }
  .loading-container {
    display: flex;
    justify-content: center;
    gap: 4px;
    margin-top: 1rem;
    height: 56px;
    align-items: center;
    color: $action-sheet-loader-color;

    .loading-text {
      font-size: $size-xs;
      font-weight: 500;
    }
  }
}
.form {
  padding: 0 24px;
  & > * {
    margin-bottom: 12px;
    &:last-child {
      margin-bottom: 24px;
    }
  }
  &.pd-20 {
    padding-left: 2rem;
    padding-right: 2rem;
  }

  .title-wrapper {
    font-size: $size-m;
    display: flex;
    flex-direction: column;
    color: $input-form-title-color;
    font-weight: 500;
    margin: 0 auto 16px;
    &.center {
      display: flex;
      justify-content: center;
      align-items: center;
    }
    .error-msg {
      color: $input-form-error-message-text-color;
      font-size: $size-xs;
      max-width: 22.3rem;
      display: flex;
      justify-content: center;
      align-items: center;
      margin: 0.4rem 0;
      .err-img {
        height: 1.5rem;
        margin-right: 0.8rem;
        img {
          width: 100%;
          height: 100%;
        }
      }
    }
  }
  .refer-info {
    font-size: $size-m;
  }
}

.linklike-button {
  font-size: 14px;
  @extend .text-medium;
  color: $login-form-link-text;
  text-align: center;
  padding-top: 9px;
  text-decoration: underline;
  cursor: pointer;
}

.cancel-request-icon {
  position: absolute;
  cursor: pointer;
  z-index: 3;
  top: -15px;
  right: 10px;
  font-size: 1.8rem;
  padding: 7px 20px 15px;
  background: $chat-input-container-background;
  // border-radius: 67% 67% 10% 14% / 57% 57% 0% 0%;
  // border-radius: 79% 79% 0% 0% / 100% 100% 0% 0% ;
  border-radius: 50% 50% 0% 0% / 100% 100% 0% 0%;
  box-shadow: 0 -3px 5px 0 rgba(0, 0, 0, 0.15);

  .cancel-icon {
    position: relative;
    z-index: 3;
  }

  &::after {
    pointer-events: none;
    content: ' ';
    position: absolute;
    color: transparent;
    top: 15px;
    bottom: -1px;
    right: -4px;
    left: -4px;
    z-index: 2;
    background: $chat-input-container-background;
  }
}
</style>
